<script setup lang="ts">
/**
 * The signage42 wordmark.
 *
 * Geometry is taken from the reference artwork rather than eyeballed, measured
 * against a 44px render of it:
 *
 *   - `42` is 0.52em of the wordmark (digit height 16px against a 23px
 *     x-height), not the ~0.8em a browser's default `<sup>` would give;
 *   - its baseline sits 0.43em above the wordmark's. The offset resolves `em`
 *     against the raised element's own size, hence 0.83em below
 *     (0.83 × 0.52 = 0.43);
 *   - both halves are the same colour. The `42` used to be `--lv-accent` gold;
 *     the artwork has no gold pixel in it at all, so the mark now inherits one
 *     colour from its caller and works on any ground.
 *
 * Weight is the one deliberate deviation: the reference's stems are heavier
 * than 700, but IBM Plex Sans Variable stops there, and a second family for one
 * word is not worth the request.
 *
 * Size and colour belong to the caller — `<BrandMark class="text-xl" />`.
 *
 * It lives in this package because it is the only Vue package both apps already
 * depend on, and the mark now carries enough CSS that a second copy of it in the
 * player would drift from this one. A `packages/brand` holding one component is
 * more wiring than that is worth. The family is named here rather than borrowed
 * from the web app's `.font-display`, because the player has no such class —
 * keep the two in step if the display face ever changes.
 */
</script>

<template>
  <span class="brand">signage<span class="brand-superscript">42</span></span>
</template>

<style scoped>
.brand {
  font-family: 'IBM Plex Sans Variable', ui-sans-serif, system-ui, sans-serif;
  font-weight: 700;
  letter-spacing: -0.02em;
  white-space: nowrap;
}

.brand-superscript {
  font-size: 0.52em;
  font-weight: 400;
  /* Without this the mark is 3px taller than the plain wordmark was: a smaller
     font sharing the parent's fixed line-height gets a deeper half-leading
     below the baseline, and the line box grows to hold it. The raised box is
     painted, so its own height is never seen. */
  line-height: 1;
  /* Raised by paint, not by layout: `vertical-align` here grows the line box,
     which made the sticky header 5.6px taller. `position: relative` leaves the
     box on the baseline, so the header keeps the height it had.
     0.83 × 0.52em = 0.43em of the wordmark. See the block comment. */
  position: relative;
  top: -0.83em;
  letter-spacing: 0;
  /* The wordmark's -0.02em tracking pulls the 4 into the e; the reference
     keeps a hair of air there. 0.08 × 0.52em ≈ 0.04em of the wordmark. */
  margin-left: 0.08em;
}
</style>
